import * as React from 'react'
import { api } from '../../../../util/api'
import { useAylaTimeout } from '~/components/shared/AylaTimeout'

export const useAiResponseStream = () => {
  const [aiResponse, setAiResponse] = React.useState('')
  const [coverLetterId, setCoverLetterId] = React.useState(null)
  const [aiStreamComplete, setAiStreamComplete] = React.useState(false)
  const [serverSentEvents, setServerSentEvents] = React.useState(false)
  const [aiStreamEmpty, setAiStreamEmpty] = React.useState(false)
  const { timedOut, resetTimeout, pollFailed, pollSucceeded } = useAylaTimeout({
    waiting: !!coverLetterId && !aiStreamComplete && !aiStreamEmpty,
    activity: aiResponse,
  })
  const aiFailed = timedOut || aiStreamEmpty

  React.useEffect(() => {
    if (!serverSentEvents) return
    if (!coverLetterId) return
    if (timedOut) return

    const eventURL = `/story/cover_letters/create_ai_sse/${coverLetterId}?format=json`
    const eventSource = new EventSource(eventURL)
    let received = false

    eventSource.addEventListener('message', (event) => {
      received = true
      setAiResponse((r) => `${r}${event.data}`)
    })

    eventSource.addEventListener('error', (event) => {
      if (event.eventPhase === EventSource.CLOSED) {
        eventSource.close()
        if (received) {
          setAiStreamComplete(true)
        } else {
          setAiStreamEmpty(true)
        }
      }
    })

    return () => eventSource.close()
  }, [coverLetterId, serverSentEvents, timedOut])

  React.useEffect(() => {
    if (serverSentEvents) return
    if (!coverLetterId) return
    if (timedOut) return

    const pollingURL = `/story/cover_letters/create_ai_polling/${coverLetterId}`

    const interval = setInterval(async () => {
      const response = await api.get(pollingURL).catch(() => null)
      if (!response) {
        pollFailed()
        return
      }
      pollSucceeded()
      const {
        data: { content, completed },
      } = response
      setAiResponse(`${content}`)
      setAiStreamComplete(completed)
      if (completed) {
        document.getElementById('copy-button').style.display = 'flex'
        document.getElementById('download-button-container').style.display =
          'block'
        document.getElementById('ayla-cover-letter-text').style.display =
          'block'
        document.getElementById('cover-letter-buttons').style.display = 'flex'
        document.getElementById('continue-button').style.display = 'block'
        clearInterval(interval)
        setAiResponse('')
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [
    coverLetterId,
    aiResponse,
    aiStreamComplete,
    serverSentEvents,
    timedOut,
    pollFailed,
    pollSucceeded,
  ])

  const resetAiResponse = React.useCallback(() => {
    setCoverLetterId(null)
    setAiResponse('')
    setAiStreamComplete(false)
    setAiStreamEmpty(false)
    resetTimeout()
  }, [resetTimeout])

  return {
    aiResponse,
    coverLetterId,
    setCoverLetterId,
    aiStreamComplete,
    setServerSentEvents,
    aiFailed,
    resetAiResponse,
  }
}
